import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { readScopedLocalJson, writeScopedLocalJson } from '@/lib/offline-core';
import {
  getGuideBuckets,
  getGuideSurfaceSection,
  GUIDE_ALL_BUCKETS,
  type GuideSurfaceSection,
} from '../../../ui-foundation/features/guides';

const GUIDE_CACHE_SCOPE = 'guides';

function guideCacheKey(section?: GuideSurfaceSection | null) {
  return section || 'all';
}

export interface PlaybookStep {
  step: number;
  title: string;
  description: string;
  actions: string[];
  links?: { label: string; url: string }[];
  call_script?: string | null;
  tip?: string | null;
}

export interface Playbook {
  id: string;
  section: string;
  title: string;
  description: string;
  icon: string;
  difficulty: string | null;
  estimated_time: string | null;
  life_event_tags: string[] | null;
  steps: PlaybookStep[];
  is_active: boolean;
  sort_order: number;
  last_reviewed_at: string;
  review_frequency_months: number;
  created_at: string;
  updated_at: string;
}

export interface ContentCard {
  id: string;
  section: string;
  title: string;
  summary: string;
  body: string;
  detail: string | null;
  icon: string | null;
  link_label: string | null;
  link_url: string | null;
  last_reviewed_at: string | null;
  sort_order: number;
}

export type GuideItem =
  | ({ kind: 'playbook' } & Playbook)
  | ({ kind: 'content-card' } & ContentCard);

export type HelperPackGuideItem = Extract<GuideItem, { kind: 'playbook' }>;
export type SupportCardGuideItem = Extract<GuideItem, { kind: 'content-card' }>;

function normaliseGuideText(value: string | null | undefined) {
  return (value || '').trim().toLowerCase();
}

export function capitalise(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function fallbackHelperPackIcon(title: string) {
  const value = title.toLowerCase();

  if (value.includes('moving')) return '📦';
  if (value.includes('repair') || value.includes('maintenance')) return '🧰';
  if (value.includes('rent') || value.includes('tenancy') || value.includes('housing') || value.includes('home')) return '🏠';
  if (value.includes('school') || value.includes('childcare') || value.includes('calendar')) return '🎒';
  if (value.includes('co-parent') || value.includes('family') || value.includes('care')) return '🫶';
  if (value.includes('benefit') || value.includes('money')) return '💰';
  if (value.includes('debt') || value.includes('creditor') || value.includes('arrears')) return '🧾';
  if (value.includes('budget') || value.includes('bill')) return '💸';
  if (value.includes('statement') || value.includes('bank')) return '🏦';
  if (value.includes('survival') || value.includes('emergency') || value.includes('council')) return '🛟';
  if (value.includes('self-employed') || value.includes('invoice') || value.includes('hmrc')) return '💼';
  if (value.includes('rights') || value.includes('redundancy') || value.includes('discrimination') || value.includes('formal')) return '⚖️';

  return '🗂️';
}

export function helperPackFooterLabel(guide: GuideItem) {
  if (guide.kind === 'playbook') {
    if (guide.estimated_time) return guide.estimated_time;
    return `${guide.steps.length} step${guide.steps.length === 1 ? '' : 's'} in this guide`;
  }

  return guide.link_label || 'Open support guide';
}

export function splitGuideItems(guides: GuideItem[]) {
  const helperPacks: HelperPackGuideItem[] = [];
  const supportCards: SupportCardGuideItem[] = [];

  guides.forEach((guide) => {
    if (guide.kind === 'playbook') {
      helperPacks.push(guide);
      return;
    }

    supportCards.push(guide);
  });

  return { helperPacks, supportCards };
}

export function filterGuideItems(guides: GuideItem[], query: string) {
  const needle = normaliseGuideText(query);
  if (!needle) {
    return guides;
  }

  return guides.filter((guide) => {
    const haystack = [
      guide.title,
      guide.kind === 'playbook' ? guide.description : guide.summary,
      guide.kind === 'content-card' ? guide.body : '',
      guide.kind === 'content-card' ? guide.detail : '',
      guide.kind === 'playbook'
        ? guide.steps
            .map((step) => `${step.title} ${step.description} ${step.actions.join(' ')} ${step.call_script || ''} ${step.tip || ''}`)
            .join(' ')
        : '',
    ]
      .map(normaliseGuideText)
      .join(' ');

    return haystack.includes(needle);
  });
}

function sortGuides(left: GuideItem, right: GuideItem) {
  const leftSort = 'sort_order' in left ? left.sort_order : 0;
  const rightSort = 'sort_order' in right ? right.sort_order : 0;
  if (leftSort !== rightSort) {
    return leftSort - rightSort;
  }

  return left.title.localeCompare(right.title);
}

export async function fetchGuides(section?: GuideSurfaceSection | null) {
  const cacheKey = guideCacheKey(section);
  const sections = section ? getGuideBuckets(section) : GUIDE_ALL_BUCKETS;
  try {
    const [playbooksResult, contentCardsResult] = await Promise.all([
      supabase
        .from('playbooks')
        .select('*')
        .in('section', sections)
        .eq('is_active', true)
        .order('sort_order'),
      supabase
        .from('content_cards')
        .select('id, section, title, summary, body, detail, icon, link_label, link_url, last_reviewed_at, sort_order')
        .in('section', sections)
        .eq('is_active', true)
        .order('sort_order'),
    ]);

    if (playbooksResult.error) throw playbooksResult.error;
    if (contentCardsResult.error) throw contentCardsResult.error;

    const guides = [
      ...(((playbooksResult.data as Playbook[]) || []).map((item) => ({ ...item, kind: 'playbook' as const }))),
      ...(((contentCardsResult.data as ContentCard[]) || []).map((item) => ({ ...item, kind: 'content-card' as const }))),
    ].sort(sortGuides);

    writeScopedLocalJson(GUIDE_CACHE_SCOPE, cacheKey, guides);
    return guides;
  } catch (error) {
    const cached = readScopedLocalJson<GuideItem[]>(GUIDE_CACHE_SCOPE, cacheKey);
    if (cached?.length) {
      return cached;
    }
    throw error;
  }
}

export function matchesGuideSection(section: GuideSurfaceSection, guideSection: string) {
  return getGuideBuckets(section).includes(guideSection);
}

export function useGuides(section?: GuideSurfaceSection | null) {
  const [guides, setGuides] = useState<GuideItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const nextGuides = await fetchGuides(section);
        if (!cancelled) {
          setGuides(nextGuides);
        }
      } catch (loadError: any) {
        if (!cancelled) {
          console.error(loadError);
          setGuides([]);
          setError(loadError?.message || 'Could not load guides right now.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [section]);

  return { guides, loading, error };
}

export function useGuideGroups(section?: GuideSurfaceSection | null) {
  const { guides, loading, error } = useGuides(section);

  const groups = useMemo(() => {
    const grouped = new Map<GuideSurfaceSection, GuideItem[]>();

    for (const guide of guides) {
      const guideSection = getGuideSurfaceSection(guide.section);
      if (!guideSection) continue;
      const existing = grouped.get(guideSection) || [];
      existing.push(guide);
      grouped.set(guideSection, existing);
    }

    return grouped;
  }, [guides]);

  return { groups, guides, loading, error };
}

export function buildGuideDetailPath(section: GuideSurfaceSection, guide: GuideItem) {
  return `/guides/${section}/${guide.kind}/${guide.id}`;
}
